import React, { memo, useMemo, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { scaleLinear } from 'd3-scale';
import { line } from 'd3-shape';

import { useHorizontalScroll } from 'hooks';
import {
  CHART_WATERMARK_IMAGE,
  getChartWatermarkCoords,
} from 'utils/chartWatermark';

import Path from 'components/Path';
import Apex from 'components/Apex';

import {
  Chart,
  ChartContainer,
  ChartWatermark,
  ChartFocus,
  XAxisLabel,
  YAxisLabel,
  AxisLine,
  Empty,
} from './styles';

const SEGMENT_WIDTH = 48;
const CHART_PLOT_BOTTOM_OFFSET = 38;

const getChartDimensions = props => ({
  chartWidth: Math.max(
    props.width - props.offset * 3,
    props.dotsCount * SEGMENT_WIDTH
  ),
  chartHeight: props.height - props.offset * 2,
});

const createScales = props => {
  const { chartWidth, chartHeight } = getChartDimensions(props);

  const isScroll = chartWidth !== props.width - props.offset * 3;

  const scaleWidth = scaleLinear()
    .domain([0, isScroll ? props.dotsCount : props.dotsCount - 1])
    .range([0, chartWidth]);

  const scaleHeight = scaleLinear()
    .domain(props.extent)
    .range([chartHeight, 0]);

  const scaleLine = line()
    .x((item, index) => scaleWidth(index))
    .y(item => scaleHeight(item));

  return {
    isScroll,
    scaleWidth,
    scaleHeight,
    scaleLine,
    chartWidth,
    chartHeight,
  };
};

const VirtualizedMultipleLineChart = memo(
  ({
    rates,
    terms,
    periods,
    width,
    height,
    offset,
    extent,
    dotsCount,
    isLoading,
    isInitialized,
    isEmpty,
  }) => {
    /**
     * Без clientRender не работает hydrate
     */
    const [clientRender, toggleClientRender] = useState(false);

    useEffect(() => {
      toggleClientRender(true);
    }, []);

    const [offsetX, offsetY] = offset;

    const { isScroll, scaleWidth, scaleHeight, scaleLine } = useMemo(
      () =>
        createScales({
          width,
          height,
          offset: offsetX,
          extent,
          periods,
          dotsCount,
        }),
      [width, height, offsetX, extent, periods, dotsCount]
    );

    const isNoData = !isLoading && isInitialized && isEmpty;

    const lines = useMemo(() => rates.map(lineRates => scaleLine(lineRates)), [
      rates,
      scaleLine,
    ]);

    const ticksCount = useMemo(() => {
      const [min, max] = extent;
      const diff = max - min + 1;

      return Math.min(diff, 10);
    }, [extent]);

    const maxTranslateX = useMemo(
      () => width - scaleWidth(isScroll ? dotsCount : dotsCount - 1),
      [width, scaleWidth, dotsCount, isScroll]
    );

    const {
      translateX,
      handleMouseDown,
      handleTouchStart,
    } = useHorizontalScroll(null, maxTranslateX);

    const watermarkCoords = useMemo(
      () =>
        getChartWatermarkCoords({
          chartWidth: width,
          chartLeft: offsetX,
          chartBottom: height - CHART_PLOT_BOTTOM_OFFSET,
        }),
      [width, offsetX, height]
    );

    return !isNoData ? (
      <ChartContainer width={width} height={height}>
        <Chart
          width={width}
          height={height}
          onTouchStart={handleTouchStart}
          onMouseDown={handleMouseDown}
        >
          <defs>
            <linearGradient id="beforeFade" gradientTransform="rotate(0)">
              <stop offset="50%" stopColor="white" />
              <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
            </linearGradient>

            <linearGradient id="afterFade" gradientTransform="rotate(0)">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
              <stop offset="50%" stopColor="white" />
            </linearGradient>
          </defs>

          {clientRender &&
            scaleHeight.ticks(ticksCount).map(rate => {
              const y = scaleHeight(rate) + offsetY;

              return (
                <AxisLine
                  key={rate}
                  x1={offsetX}
                  x2={width - offsetX}
                  y1={y}
                  y2={y}
                />
              );
            })}

          {clientRender && (
            <ChartFocus
              transform={`translate(${translateX -
                (isScroll ? -offsetX : offsetX)},${offsetY})`}
            >
              {periods.map((period, index) => {
                const x = scaleWidth(index);

                return (
                  <AxisLine
                    key={period}
                    x1={x}
                    x2={x}
                    y1={0}
                    y2={height - 38}
                  />
                );
              })}

              {lines.map((d, index) =>
                d ? (
                  <Path key={d} type="line" d={d} color={terms[index].color} />
                ) : null
              )}

              {terms.map(({ color, value, type }, index) =>
                rates[index].map((rate, i) => (
                  <Apex
                    // eslint-disable-next-line
                    key={`${value}-${i}`}
                    x={scaleWidth(i)}
                    y={scaleHeight(rate)}
                    color={color}
                    type={type}
                    withBorder
                  />
                ))
              )}

              {periods.map((period, index, array) => (
                <XAxisLabel
                  key={period}
                  x={scaleWidth(index) - offsetX}
                  y={height - 20}
                  bold={array.length - 1 === index}
                >
                  {periods[index]}
                </XAxisLabel>
              ))}
            </ChartFocus>
          )}

          {clientRender && translateX < 0 && (
            <rect
              width="24"
              height={height}
              x="0"
              y="0"
              fill={'url("#beforeFade")'}
            />
          )}

          {clientRender && translateX !== maxTranslateX && (
            <rect
              width="24"
              height={height}
              x={width - offsetX}
              y="0"
              fill={'url("#afterFade")'}
            />
          )}

          {clientRender &&
            scaleHeight.ticks(ticksCount).map(rate => (
              <YAxisLabel key={rate} x="0" y={scaleHeight(rate) + offsetY + 12}>
                {`${rate}`}
              </YAxisLabel>
            ))}
        </Chart>

        {clientRender && (
          <ChartWatermark
            src={CHART_WATERMARK_IMAGE}
            alt=""
            aria-hidden="true"
            style={{
              left: `${watermarkCoords.x}px`,
              top: `${watermarkCoords.y}px`,
              height: `${watermarkCoords.height}px`,
              width: 'auto',
            }}
          />
        )}
      </ChartContainer>
    ) : (
      <Empty>
        <FormattedMessage id="empty" />
      </Empty>
    );
  }
);

VirtualizedMultipleLineChart.propTypes = {
  rates: PropTypes.arrayOf(PropTypes.array).isRequired,
  terms: PropTypes.arrayOf(PropTypes.object).isRequired,
  periods: PropTypes.arrayOf(PropTypes.string).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  offset: PropTypes.arrayOf(PropTypes.number),
  extent: PropTypes.arrayOf(PropTypes.number),
  dotsCount: PropTypes.number,
  isLoading: PropTypes.bool,
  isInitialized: PropTypes.bool,
  isEmpty: PropTypes.bool,
};

VirtualizedMultipleLineChart.defaultProps = {
  width: 600,
  height: 364,
  offset: [24, 16],
  extent: [0, 0],
  dotsCount: 0,
  isLoading: false,
  isInitialized: false,
  isEmpty: false,
};

export default VirtualizedMultipleLineChart;
